const ATTENTION_WINDOW_MS = 72 * 60 * 60 * 1000;

export type SessionOrderingItem<T> = Readonly<{
	item: T;
	id: string;
	state?: string;
	stateChangedAt?: number;
	recencyAt?: number;
}>;

export type PrioritizedSessions<T> = Readonly<{
	promoted: readonly T[];
	ordinary: readonly T[];
	ordered: readonly T[];
	nextExpiryAt?: number;
}>;

const compareRecency = <T>(a: SessionOrderingItem<T>, b: SessionOrderingItem<T>): number => {
	const difference = (b.recencyAt ?? -Infinity) - (a.recencyAt ?? -Infinity);
	if (Number.isFinite(difference) && difference !== 0) {
		return difference;
	}
	return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
};

/** Orders only the caller's loaded rows; state-change time never substitutes for recency. */
export const prioritizeSessions = <T>(
	items: readonly SessionOrderingItem<T>[],
	now: number,
): PrioritizedSessions<T> => {
	const promoted: SessionOrderingItem<T>[] = [];
	const ordinary: SessionOrderingItem<T>[] = [];
	let nextExpiryAt: number | undefined;

	for (const entry of items) {
		const changedAt = entry.stateChangedAt;
		const expiresAt = changedAt == null ? Number.NaN : changedAt + ATTENTION_WINDOW_MS;
		if (
			(entry.state === 'INPUT_REQUIRED' || entry.state === 'AUTH_REQUIRED') &&
			changedAt != null &&
			Number.isFinite(changedAt) &&
			changedAt > 0 &&
			changedAt <= now &&
			now < expiresAt
		) {
			promoted.push(entry);
			nextExpiryAt = Math.min(nextExpiryAt ?? expiresAt, expiresAt);
		} else {
			ordinary.push(entry);
		}
	}

	const promotedItems = promoted.sort(compareRecency).map(({ item }) => item);
	const ordinaryItems = ordinary.map(({ item }) => item);
	return {
		promoted: promotedItems,
		ordinary: ordinaryItems,
		ordered: [...promotedItems, ...ordinaryItems],
		nextExpiryAt,
	};
};
